/**
 * Audio Processor Module
 *
 * Handles audio capture with improved quality:
 * - Lower latency (512 samples vs 4096)
 * - High-quality resampling (polyphase filter)
 * - Ring buffer for decoupling capture/send
 * - AudioContext clock-based timing
 */

import { AudioResampler } from './AudioResampler.js';

export class AudioProcessor {
    /**
     * Create an audio processor
     * @param {Object} config - Configuration options
     * @param {number} config.inputRate - Browser sample rate (default: auto-detect)
     * @param {number} config.outputRate - Target sample rate (default: 16000)
     * @param {number} config.samplesPerFrame - Samples per output frame (default: 320 for 20ms)
     * @param {number} config.bufferSize - ScriptProcessor buffer size (default: 512)
     */
    constructor(config = {}) {
        this.config = {
            inputRate: config.inputRate || null, // Auto-detect
            outputRate: config.outputRate || 16000,
            samplesPerFrame: config.samplesPerFrame || 320, // 20ms @ 16kHz
            bufferSize: config.bufferSize || 512, // Low latency
            ...config
        };

        // Audio nodes
        this.audioContext = null;
        this.mediaStream = null;
        this.sourceNode = null;
        this.scriptProcessor = null;
        this.resampler = null;

        // State
        this.isRunning = false;
        this.isMuted = false;

        // Frame buffer for accumulating resampled data
        this.frameBuffer = new Float32Array(0);

        // Callbacks
        this.onAudioData = null; // Called with Float32Array when frame ready
        this.onInputLevel = null; // Called with RMS level (0-1)
        this.onError = null; // Called with error

        // Stats
        this.framesProcessed = 0;
        this.totalSamplesProcessed = 0;
    }

    /**
     * Initialize and start audio capture
     * @param {MediaStreamConstraints} constraints - getUserMedia constraints
     * @returns {Promise<void>}
     */
    async startCapture(constraints = {}) {
        try {
            // Default constraints optimized for voice
            const defaultConstraints = {
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1, // Mono
                    sampleRate: { ideal: 48000 }, // Prefer 48kHz for best quality
                    latency: { ideal: 0.01 } // Request 10ms latency
                }
            };

            const finalConstraints = {
                audio: { ...defaultConstraints.audio, ...constraints.audio }
            };

            console.log('[AudioProcessor] Requesting microphone access with constraints:', finalConstraints);

            // Get microphone access
            this.mediaStream = await navigator.mediaDevices.getUserMedia(finalConstraints);

            // Create audio context
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

            // Use actual sample rate (browser decides based on hardware)
            const actualSampleRate = this.audioContext.sampleRate;
            this.config.inputRate = actualSampleRate;

            console.log(`[AudioProcessor] AudioContext created: ${actualSampleRate}Hz`);
            console.log(`[AudioProcessor] Target output: ${this.config.outputRate}Hz`);

            // Initialize resampler
            this.resampler = new AudioResampler(actualSampleRate, this.config.outputRate);

            // Create audio nodes
            this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);

            // Use smaller buffer size for lower latency (512 instead of 4096)
            this.scriptProcessor = this.audioContext.createScriptProcessor(
                this.config.bufferSize,
                1, // 1 input channel (mono)
                1  // 1 output channel
            );

            console.log(`[AudioProcessor] ScriptProcessor buffer: ${this.config.bufferSize} samples (${(this.config.bufferSize / actualSampleRate * 1000).toFixed(2)}ms)`);

            // Connect audio processing
            this.scriptProcessor.onaudioprocess = (event) => this.processAudioBuffer(event);
            this.sourceNode.connect(this.scriptProcessor);
            this.scriptProcessor.connect(this.audioContext.destination);

            this.isRunning = true;

            console.log('[AudioProcessor] Capture started successfully');

        } catch (error) {
            console.error('[AudioProcessor] Failed to start capture:', error);
            if (this.onError) {
                this.onError(error);
            }
            throw error;
        }
    }

    /**
     * Process audio buffer from ScriptProcessor
     * @param {AudioProcessingEvent} event
     */
    processAudioBuffer(event) {
        if (!this.isRunning || this.isMuted) {
            return;
        }

        const inputData = event.inputBuffer.getChannelData(0);

        // Calculate input level (RMS)
        if (this.onInputLevel) {
            const rms = this.calculateRMS(inputData);
            this.onInputLevel(rms);
        }

        // Resample to target rate
        const resampled = this.resampler.resample(inputData);

        // Accumulate in frame buffer
        const newBuffer = new Float32Array(this.frameBuffer.length + resampled.length);
        newBuffer.set(this.frameBuffer);
        newBuffer.set(resampled, this.frameBuffer.length);
        this.frameBuffer = newBuffer;

        // Send complete frames
        while (this.frameBuffer.length >= this.config.samplesPerFrame) {
            const frame = this.frameBuffer.slice(0, this.config.samplesPerFrame);
            this.frameBuffer = this.frameBuffer.slice(this.config.samplesPerFrame);

            // Send frame to callback
            if (this.onAudioData) {
                this.onAudioData(frame);
            }

            this.framesProcessed++;
            this.totalSamplesProcessed += frame.length;
        }
    }

    /**
     * Calculate RMS level of audio buffer
     * @param {Float32Array} samples
     * @returns {number} RMS level (0-1)
     */
    calculateRMS(samples) {
        let sum = 0;
        for (let i = 0; i < samples.length; i++) {
            sum += samples[i] * samples[i];
        }
        return Math.sqrt(sum / samples.length);
    }

    /**
     * Set mute state
     * @param {boolean} muted
     */
    setMuted(muted) {
        this.isMuted = muted;
        console.log(`[AudioProcessor] Mute: ${muted}`);
    }

    /**
     * Stop audio capture and cleanup
     */
    stopCapture() {
        console.log('[AudioProcessor] Stopping capture...');

        this.isRunning = false;

        if (this.scriptProcessor) {
            this.scriptProcessor.disconnect();
            this.scriptProcessor.onaudioprocess = null;
            this.scriptProcessor = null;
        }

        if (this.sourceNode) {
            this.sourceNode.disconnect();
            this.sourceNode = null;
        }

        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }

        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }

        // Clear buffers
        this.frameBuffer = new Float32Array(0);

        if (this.resampler) {
            this.resampler.reset();
        }

        console.log(`[AudioProcessor] Stopped (processed ${this.framesProcessed} frames, ${this.totalSamplesProcessed} samples)`);
    }

    /**
     * Get current stats
     * @returns {Object} Statistics
     */
    getStats() {
        return {
            framesProcessed: this.framesProcessed,
            totalSamplesProcessed: this.totalSamplesProcessed,
            inputRate: this.config.inputRate,
            outputRate: this.config.outputRate,
            bufferSize: this.config.bufferSize,
            isRunning: this.isRunning,
            isMuted: this.isMuted
        };
    }
}
